import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ConfirmDialog from "../../../../Components/ConfirmDialog/ConfirmDialog";
import { getBudgetStatus, AnimatedBudgetBadge } from "../../../../Components/BudgetBadge/BudgetBadge";

// Format number with commas and 2 decimal places for currency
const formatNumber = (num) => {
  if (num === null || num === undefined || num === "") return "";
  const number = parseFloat(num);
  if (isNaN(number)) return "";
  return number.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const CATEGORY_HELP_TOOLTIP =
  "To add or edit categories (and set budgets), go to Settings → Categories.";

const CategoryHelpIcon = () => (
  <Link
    to="/settings?expand=categories"
    className="ml-1 inline-flex text-slate-400 transition-colors hover:text-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 rounded"
    title={CATEGORY_HELP_TOOLTIP}
    aria-label={CATEGORY_HELP_TOOLTIP}
  >
    <svg
      className="h-3.5 w-3.5"
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
    </svg>
  </Link>
);

// Parse formatted number back to raw value
const parseNumber = (str) => {
  if (!str) return "";
  // Remove commas and other formatting
  const cleaned = str.replace(/,/g, "");
  const number = parseFloat(cleaned);
  return isNaN(number) ? "" : number;
};

// Editable cell component - only updates on blur
const EditableCell = ({
  value,
  onBlur,
  type = "text",
  className = "",
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");
  const [isFocused, setIsFocused] = useState(false);
  const [lastSubmittedValue, setLastSubmittedValue] = useState(null);

  // Sync local value with prop value ONLY when prop changes from parent
  useEffect(() => {
    // Skip if we're focused (user is editing)
    if (isFocused) return;
    // Skip if this is our own submitted value coming back
    if (lastSubmittedValue !== null && value === lastSubmittedValue) {
      return;
    }
    setLocalValue(value ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // Only depend on value, not isFocused

  // Always show local value (optimistic update)
  const displayValue = localValue;

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
    setLastSubmittedValue(null); // Reset on new focus
  };

  const handleChange = (e) => {
    if (disabled) return;
    setLocalValue(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const handleBlur = () => {
    if (disabled) return;
    setIsFocused(false);
    // Only trigger API update if value actually changed
    if (localValue !== (value ?? "")) {
      setLastSubmittedValue(localValue);
      onBlur({ target: { value: localValue } });
    }
  };

  return (
    <input
      type={type}
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="-" 
      disabled={disabled}
      readOnly={disabled}
      className={`w-full bg-transparent px-2 py-1 text-left text-base text-slate-900 placeholder-slate-400 outline-none focus:bg-slate-50 ${disabled ? 'cursor-not-allowed opacity-60' : ''} ${className}`}
      style={{ fontSize: '16px' }}
    />
  );
};

// Integer cell component - removes leading zeros on input
const IntegerCell = ({
  value,
  onBlur,
  className = "",
  disabled = false,
}) => {
  const [localValue, setLocalValue] = useState(value ?? "");
  const [isFocused, setIsFocused] = useState(false);
  const [lastSubmittedValue, setLastSubmittedValue] = useState(null);

  // Sync local value with prop value ONLY when prop changes from parent
  useEffect(() => {
    if (isFocused) return;
    if (lastSubmittedValue !== null && Number(value) === Number(lastSubmittedValue)) {
      return;
    }
    setLocalValue(value ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
    setLastSubmittedValue(null);
  };

  const handleChange = (e) => {
    if (disabled) return;
    let newValue = e.target.value;
    // Remove leading zeros (e.g., "019" -> "19"), but keep "0" as valid
    if (newValue.length > 1 && newValue.startsWith("0")) {
      newValue = newValue.replace(/^0+/, "") || "0";
    }
    // Only allow digits
    newValue = newValue.replace(/[^0-9]/g, "");
    setLocalValue(newValue);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const handleBlur = () => {
    if (disabled) return;
    setIsFocused(false);
    const parsed = parseInt(localValue, 10);
    const finalValue = isNaN(parsed) ? "" : parsed;
    setLocalValue(finalValue);
    
    // Always trigger the callback - server handles the logic
    setLastSubmittedValue(finalValue);
    onBlur({ target: { value: finalValue } });
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="-" 
      disabled={disabled}
      readOnly={disabled}
      className={`w-full bg-transparent px-2 py-1 text-left text-base text-slate-900 placeholder-slate-400 outline-none focus:bg-slate-50 ${disabled ? 'cursor-not-allowed opacity-60' : ''} ${className}`}
      style={{ fontSize: '16px' }}
    />
  );
};

// Number cell with formatting - only updates on blur
const NumberCell = ({
  value,
  onBlur,
  className = "",
  disabled = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localValue, setLocalValue] = useState(value ?? "");
  const [lastSubmittedValue, setLastSubmittedValue] = useState(null);

  // Sync local value with prop value ONLY when prop changes from parent
  // Not when focus changes
  useEffect(() => {
    // Skip if we're focused (user is editing)
    if (isFocused) return;
    // Skip if this is our own submitted value coming back
    if (lastSubmittedValue !== null && Number(value) === Number(lastSubmittedValue)) {
      return;
    }
    setLocalValue(value ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]); // Only depend on value, not isFocused

  // Display formatted local value when not focused, raw value when focused
  const displayValue = isFocused ? localValue : formatNumber(localValue);

  const handleFocus = () => {
    if (disabled) return;
    setIsFocused(true);
    setLastSubmittedValue(null); // Reset on new focus
  };

  const handleChange = (e) => {
    if (disabled) return;
    let newValue = e.target.value;
    
    // Remove leading zeros (e.g., "0123" -> "123", "0123.45" -> "123.45")
    // But keep "0" as valid, and allow "0." or "0," for decimal input
    if (newValue.length > 1 && newValue.startsWith("0")) {
      // Check if it's "0." or "0," - these are valid for decimal input
      if (newValue[1] === "." || newValue[1] === ",") {
        // Keep "0." or "0," as is
      } else {
        // Remove leading zeros before any decimal point
        // Split by decimal point to handle both "." and ","
        const parts = newValue.split(/[.,]/);
        if (parts[0]) {
          // Remove leading zeros from the integer part
          parts[0] = parts[0].replace(/^0+/, "") || "0";
          // Rejoin with the original decimal separator if it exists
          const decimalSeparator = newValue.includes(".") ? "." : (newValue.includes(",") ? "," : "");
          newValue = parts.join(decimalSeparator);
        }
      }
    }
    
    setLocalValue(newValue);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === "Enter") {
      e.target.blur();
    }
  };

  const handleBlur = () => {
    if (disabled) return;
    setIsFocused(false);
    const parsed = parseNumber(localValue);
    // Update local value to parsed number (optimistic update)
    setLocalValue(parsed);
    // Only trigger API update if value actually changed
    const currentValue = Number(value) || 0;
    const newValue = Number(parsed) || 0;
    if (newValue !== currentValue) {
      setLastSubmittedValue(parsed);
      onBlur({ target: { value: parsed } });
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={displayValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder="-" 
      disabled={disabled}
      readOnly={disabled}
      className={`w-full bg-transparent px-2 py-1 text-left text-base text-slate-900 placeholder-slate-400 outline-none focus:bg-slate-50 ${disabled ? 'cursor-not-allowed opacity-60' : ''} ${className}`}
      style={{ fontSize: '16px' }}
    />
  );
};

// Select cell component - updates on change (selects behave differently)
const SelectCell = ({
  value,
  options,
  onBlur,
  className = "",
  disabled = false,
}) => {
  const handleChange = (e) => {
    if (disabled) return;
    // For select, we trigger on change since user expects immediate feedback
    onBlur(e);
  };

  return (
    <select
      value={value || ""}
      onChange={handleChange}
      disabled={disabled}
      className={`w-full cursor-pointer bg-transparent px-2 py-1 text-left text-sm text-slate-900 outline-none focus:bg-slate-50 ${disabled ? 'cursor-not-allowed opacity-60' : ''} ${className}`}
    >
      <option value="">Select...</option>
      {options?.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
};

const TableLine = (props) => {
  const [expanded, setExpanded] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const rowRef = useRef(null);
  
  // Local state for optimistic updates in card header
  const [localTitle, setLocalTitle] = useState(props.title);
  const [localCategory, setLocalCategory] = useState(props.category);
  const [localSum, setLocalSum] = useState(props.sum);
  const [localPayments, setLocalPayments] = useState(props.payments);

  // Check if we're viewing the original month (for fixed expenses)
  // Infinite expenses (to_date = 999999) are always editable
  const isOriginalMonth = () => {
    if (!props.showPayments || !props.from_date || !props.currentDate) return true;
    
    // If it's an infinite expense (to_date = 999999), always allow editing
    if (props.to_date === 999999) return true;
    
    // For finite expenses, only allow editing in the original month
    // Parse currentDate from "MM-YYYY" format to YYYYMM integer
    const dateParts = props.currentDate.split("-");
    const currentDateInt = parseInt(dateParts[1] + dateParts[0], 10);
    
    return currentDateInt === props.from_date;
  };

  const isEditingAllowed = isOriginalMonth();

  // Expand and scroll to this row if expenseId matches
  useEffect(() => {
    if (props.expenseId && props.id) {
      // Compare as strings to handle both string and number IDs
      const expenseIdStr = String(props.expenseId);
      const idStr = String(props.id);
      if (expenseIdStr === idStr) {
        setExpanded(true);
        // Scroll to the row after a short delay to ensure it's rendered
        setTimeout(() => {
          if (rowRef.current) {
            rowRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 300);
      }
    }
  }, [props.expenseId, props.id]);

  // Sync with props when they change from parent
  useEffect(() => {
    setLocalTitle(props.title);
  }, [props.title]);
  
  useEffect(() => {
    setLocalCategory(props.category);
  }, [props.category]);
  
  useEffect(() => {
    setLocalSum(props.sum);
  }, [props.sum]);
  
  useEffect(() => {
    setLocalPayments(props.payments);
  }, [props.payments]);

  // Only trigger save on blur (focus out) - no onChange updates
  const handleBlur = (field, e) => {
    let value = e.target.value;
    
    // Parse payments as integer to remove leading zeros (e.g., "019" -> 19)
    if (field === "payments") {
      const parsed = parseInt(value, 10);
      value = isNaN(parsed) ? "" : parsed;
    }
    
    // Optimistic update for card header
    if (field === "title") {
      setLocalTitle(value);
    } else if (field === "category") {
      setLocalCategory(value);
    } else if (field === "sum") {
      setLocalSum(value);
    } else if (field === "payments") {
      setLocalPayments(value);
    }
    
    props.saveUpdateValueHandler(
      props.name,
      props.index,
      field,
      { value }, // Pass parsed value
      props.id,
      props.from_date,
      props.to_date,
      props.updated
    );
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = () => {
    props.delete_rec_by_id(props.table_title, props.id);
    setShowDeleteConfirm(false);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  // Navigate to the original month when clicking on a locked record
  const handleNavigateToOriginalMonth = () => {
    if (!isEditingAllowed && props.from_date && props.selectDateHandler) {
      // Convert from_date (YYYYMM) to "MM-YYYY" format for selectDateHandler
      const year = Math.floor(props.from_date / 100);
      const month = props.from_date % 100;
      const formattedDate = `${month.toString().padStart(2, '0')}-${year}`;
      props.selectDateHandler(formattedDate);
    }
  };

  const formatCurrency = (amount) => {
    const num = Number(amount) || 0;
    return props.symbol + num.toLocaleString();
  };

  // Monthly payment for fixed expenses: total payments from from_date to to_date, then sum / totalPayments
  const getMonthlyPayment = () => {
    if (!props.showPayments || props.sum == null) return null;
    const sum = Number(props.sum) || 0;
    if (props.to_date === 999999 || !props.from_date || !props.to_date) return sum; // ongoing or single month = sum is monthly
    // If explicitly marked as NOT a payment expense, sum is already monthly
    if (props.is_payment === false) return sum;
    // is_payment === true OR null (old data) → divide by months
    const fromYear = Math.floor(props.from_date / 100);
    const fromMonth = props.from_date % 100;
    const toYear = Math.floor(props.to_date / 100);
    const toMonth = props.to_date % 100;
    const totalPayments = (toYear - fromYear) * 12 + (toMonth - fromMonth) + 1;
    return totalPayments > 0 ? sum / totalPayments : sum;
  };

  const monthlyPayment = getMonthlyPayment();

  // Confirmation dialog (shared between desktop and mobile)
  const confirmDialog = (
    <ConfirmDialog
      isOpen={showDeleteConfirm}
      onConfirm={handleDeleteConfirm}
      onCancel={handleDeleteCancel}
      title="Delete Expense"
      message={`Are you sure you want to delete "${props.title || "this item"}"? This action cannot be undone.`}
      confirmText="Delete"
      cancelText="Cancel"
      variant="danger"
    />
  );

  // Desktop Table Row
  if (props.isDesktop) {
    // Format the from_date for display (YYYYMM -> Month YYYY)
    const getFormattedStartDate = () => {
      if (!props.from_date) return "";
      const year = Math.floor(props.from_date / 100);
      const month = props.from_date % 100;
      const date = new Date(year, month - 1);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    };

    return (
      <>
        <tr 
          ref={rowRef} 
          className={`group transition-colors ${!isEditingAllowed ? 'cursor-pointer hover:bg-amber-50' : 'hover:bg-slate-50'}`}
          onClick={!isEditingAllowed ? handleNavigateToOriginalMonth : undefined}
          title={!isEditingAllowed ? 'Click to navigate to the original month to edit' : ''}
        >
          <td className="border-b border-slate-200 px-4 py-2">
            <div className="flex items-center gap-2">
              <EditableCell
                value={props.title}
                onBlur={(e) => handleBlur("title", e)}
                disabled={!isEditingAllowed}
              />
              {props.showPayments && props.from_date && (
                <div className="flex-shrink-0" title={isEditingAllowed ? `Started: ${getFormattedStartDate()}` : `Started: ${getFormattedStartDate()}. Navigate to this month to edit.`}>
                  <svg 
                    className={`h-4 w-4 ${isEditingAllowed ? 'text-slate-400' : 'text-amber-500'}`}
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          </td>
          <td className="border-b border-slate-200 px-4 py-2">
            <div className="flex items-center gap-2">
              <SelectCell
                value={props.category}
                options={props.categories}
                onBlur={(e) => handleBlur("category", e)}
                disabled={!isEditingAllowed}
              />
              {props.categoriesWithBudget && props.category && props.categoriesWithBudget[props.category] && (() => {
                const budgetInfo = props.categoriesWithBudget[props.category];
                const status = getBudgetStatus(budgetInfo.budget, budgetInfo.spent);
                return (
                  <AnimatedBudgetBadge
                    status={status}
                    title={`Budget: ${formatNumber(budgetInfo.budget)}, Spent: ${formatNumber(budgetInfo.spent)}, Remaining: ${formatNumber(budgetInfo.remaining)}`}
                  />
                );
              })()}
            </div>
          </td>
          {props.showPayments && (
            <td className="border-b border-slate-200 px-4 py-2">
              <IntegerCell
                value={props.payments}
                onBlur={(e) => handleBlur("payments", e)}
                className="w-20"
                disabled={!isEditingAllowed}
              />
            </td>
          )}
          {props.showPayments && (
            <td className="border-b border-slate-200 px-4 py-2 text-slate-600">
              {monthlyPayment != null ? (
                <span className="text-sm" title="Monthly payment (used in budget total)">
                  {formatCurrency(monthlyPayment)}
                </span>
              ) : (
                <span className="text-slate-400">—</span>
              )}
            </td>
          )}
          <td className="border-b border-slate-200 px-4 py-2">
            <NumberCell
              value={props.sum}
              onBlur={(e) => handleBlur("sum", e)}
              disabled={!isEditingAllowed}
            />
          </td>
          {props.remove_enabled && (
            <td className="border-b border-slate-200 px-4 py-2">
              <button
                type="button"
                onClick={handleDeleteClick}
                disabled={!isEditingAllowed}
                className={`rounded px-3 py-1 text-xs font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                  isEditingAllowed
                    ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500 cursor-pointer'
                    : 'bg-red-300 cursor-not-allowed opacity-60'
                }`}
                title={!isEditingAllowed ? 'Can only delete in original month' : 'Delete this expense'}
              >
                Delete
              </button>
            </td>
          )}
        </tr>
        {confirmDialog}
      </>
    );
  }

  // Mobile Card View
  return (
    <div ref={rowRef} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      {/* Card Header - Always visible */}
      <div
        className="flex cursor-pointer items-center justify-between p-4"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          <svg
            className={`h-5 w-5 text-slate-400 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
          <div>
            <p className="font-medium text-slate-900">
              {localTitle || "Untitled"}
            </p>
            <div className="flex items-center gap-2">
              <p className="text-xs text-slate-500">
                {localCategory || "No category"}
              </p>
              {props.categoriesWithBudget && localCategory && props.categoriesWithBudget[localCategory] && (() => {
                const budgetInfo = props.categoriesWithBudget[localCategory];
                const status = getBudgetStatus(budgetInfo.budget, budgetInfo.spent);
                return (
                  <AnimatedBudgetBadge
                    status={status}
                    className="px-1.5"
                  />
                );
              })()}
            </div>
          </div>
        </div>
        <div className="text-right">
          <p className="font-semibold text-slate-900">
            {formatCurrency(localSum)}
          </p>
          {props.showPayments && (
            <>
              <p className="text-xs text-slate-500">
                {localPayments === 0 ? "∞" : `${localPayments} payments`}
              </p>
              {monthlyPayment != null && (
                <p className="text-xs font-medium text-indigo-600" title="Monthly payment">
                  Monthly: {formatCurrency(monthlyPayment)}
                </p>
              )}
            </>
          )}
        </div>
      </div>

      {/* Card Body - Expandable */}
      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-4">
          {/* Warning for non-original month - Clickable to navigate */}
          {!isEditingAllowed && (
            <div 
              className="mb-3 cursor-pointer rounded-md border border-amber-200 bg-amber-50 p-3 transition-colors hover:bg-amber-100"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card collapse
                handleNavigateToOriginalMonth();
              }}
            >
              <div className="flex items-start gap-2">
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    Read-only: Not original month
                  </p>
                  <p className="mt-1 text-xs text-amber-700">
                    This fixed expense can only be edited in its original month. <span className="font-semibold underline">Tap here to navigate</span> to the month it was created.
                  </p>
                </div>
                <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          )}
          <div className="space-y-3">
            {/* Title */}
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Title
              </label>
              <EditableCell
                value={props.title}
                onBlur={(e) => handleBlur("title", e)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                disabled={!isEditingAllowed}
              />
            </div>

            {/* Amount */}
            <div>
              <label className="mb-1 block text-xs font-medium text-slate-500">
                Amount ({props.symbol})
              </label>
              <NumberCell
                value={props.sum}
                onBlur={(e) => handleBlur("sum", e)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                disabled={!isEditingAllowed}
              />
            </div>

            {/* Category */}
            <div>
              <label className="mb-1 flex items-center text-xs font-medium text-slate-500">
                Category
                {!props.isDesktop && <CategoryHelpIcon />}
              </label>
              {!props.isDesktop && (
                <p className="mb-1.5 text-xs text-slate-500">
                  Tap the icon to open Settings and add or edit categories.
                </p>
              )}
              <SelectCell
                value={props.category}
                options={props.categories}
                onBlur={(e) => handleBlur("category", e)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                disabled={!isEditingAllowed}
              />
              {props.categoriesWithBudget && props.category && props.categoriesWithBudget[props.category] && (() => {
                const budgetInfo = props.categoriesWithBudget[props.category];
                const status = getBudgetStatus(budgetInfo.budget, budgetInfo.spent);
                return (
                  <div className="mt-2 rounded-md bg-slate-50 p-2 text-xs">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-slate-600">Status:</span>
                      <AnimatedBudgetBadge status={status} />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Budget:</span>
                      <span className="font-medium text-slate-900">{formatNumber(budgetInfo.budget)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Spent:</span>
                      <span className="font-medium text-slate-900">{formatNumber(budgetInfo.spent)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Remaining:</span>
                      <span className={`font-medium ${
                        budgetInfo.remaining < 0
                          ? "text-red-600"
                          : "text-slate-900"
                      }`}>
                        {formatNumber(budgetInfo.remaining)}
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Payments (for Fixed Expenses only) */}
            {props.showPayments && (
              <>
                <div>
                  <label className="mb-1 block text-xs font-medium text-slate-500">
                    Payments
                  </label>
                  <IntegerCell
                    value={props.payments}
                    onBlur={(e) => handleBlur("payments", e)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    disabled={!isEditingAllowed}
                  />
                </div>
                {monthlyPayment != null && (
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-500">
                      Monthly payment
                    </label>
                    <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-indigo-600">
                      {formatCurrency(monthlyPayment)}
                    </p>
                    <p className="mt-1 text-xs text-slate-500">
                      This amount is used in the budget total each month.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Updated timestamp */}
            {props.recored_updated && (
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">
                  Last Updated
                </label>
                <p className="text-sm text-slate-600">
                  {new Date(props.recored_updated).toLocaleString()}
                </p>
              </div>
            )}

            {/* Delete button */}
            {props.remove_enabled && (
              <div className="pt-2">
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  disabled={!isEditingAllowed}
                  className={`w-full rounded-md px-4 py-2 text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    isEditingAllowed
                      ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500 cursor-pointer'
                      : 'bg-red-300 cursor-not-allowed opacity-60'
                  }`}
                  title={!isEditingAllowed ? 'Can only delete in original month' : 'Delete this expense'}
                >
                  Delete Expense
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {confirmDialog}
    </div>
  );
};

export default TableLine;
